<script>
  import { onMount, onDestroy } from "svelte";
  import { navigate } from "svelte-routing";
  import SearchIcon from "@local/assets/icons/search.svg";
  import QuickSelectIcon from "@local/assets/icons/quick-select.svg";
  import UndoIcon from "@local/assets/icons/undo.svg";
  import RedoIcon from "@local/assets/icons/redo.svg";
  import SelectIcon from "@local/assets/icons/select.svg";
  import ZoomIcon from "@local/assets/icons/zoom.svg";
  import ZoomOutIcon from "@local/assets/icons/zoom-out.svg";
  import ZoomSelectedIcon from "@local/assets/icons/zoom-selected.svg";
  import ZoomFitIcon from "@local/assets/icons/zoom-fit.svg";
  import TableIcon from "@local/assets/icons/table.svg";
  import PanelRightIcon from "@local/assets/icons/panel-right.svg";
  import PanelLeftIcon from "@local/assets/icons/panel-left.svg";
  import WrenchIcon from "@local/assets/icons/wrench.svg";
  import GearIcon from "@local/assets/icons/gear.svg";
  import MergeIcon from "@local/assets/icons/merge.svg";
  import AiSettingsIcon from "@local/assets/icons/ai-settings.svg";
  import HelpIcon from "@local/assets/icons/help.svg";
  import GridIcon from "@local/assets/icons/grid.svg";
  import DownloadIcon from "@local/assets/icons/download.svg";
  import UploadIcon from "@local/assets/icons/upload.svg";
  import PanIcon from "@local/assets/icons/pan.svg";
  import PenPlusIcon from "@local/assets/icons/pen-plus.svg";
  import PenMinusIcon from "@local/assets/icons/pen-minus.svg";
  import HoleIcon from "@local/assets/icons/hole.svg";
  import NotchIcon from "@local/assets/icons/notch.svg";
  import EdgeFabIcon from "@local/assets/icons/edge-fabrication.svg";
  import CaretDownIcon from "@local/assets/icons/caret-down.svg";
  import CornerNotchIcon from "@local/assets/icons/corner-notch.svg";
  import LocationIcon from "@local/assets/icons/location.svg";
  import LocationItemIcon from "@local/assets/icons/location-item.svg";
  import LocationTypeIcon from "@local/assets/icons/location-type.svg";
  import HorizontalGridlineIcon from "@local/assets/icons/horizontal-gridline.svg";
  import VerticalGridlineIcon from "@local/assets/icons/vertical-gridline.svg";
  import LocationOpeningIcon from "@local/assets/icons/location-opening.svg";
  import NotesIcon from "@local/assets/icons/notes.svg";
  import AiIcon from "@local/assets/icons/ai.svg";
  import FilterIcon from "@local/assets/icons/filter.svg";
  import RulerIcon from "@local/assets/icons/ruler.svg";
  import ShareIcon from "@local/assets/icons/share.svg";

  import Tool from "./Tool.svelte";
  import AppsMenu from "#lib/framex/AppsMenu.svelte";
  import { Modal, Popover } from "svelte-utilities";

  import {
    currentTool,
    showTable,
    showLeftPanel,
    showRightPanel,
    leftPanelMode,
    itemOverlay,
    itemOverlayMode,
    showingTutorial,
  } from "#src/stores/ui.js";
  import eb from "#src/extensions/event-bus.js";
  import {
    isEdgeFabrication,
    isCornerFabrication,
    isEdgeAlignedFabrication,
  } from "@local/extensions/fabrications/is-fabrication.js";
  import { orderedList } from "@local/extensions/collections/sortable-list.js";
  import { profile } from "#src/stores/auth.js";
  import { clickOutside } from "svelte-utilities";
  import { tooltip } from "svelte-utilities";
  import { UX_MODE } from "#src/env.js";

  export let tab;
  export let path;
  export let group;
  export let disabled = false;

  let fabwarning;
  let fabPosition;
  let quote;
  let job;
  let toolDropdown;
  let showToolPanel;

  const toolNames = {
    select: "Select",
    search: "Search",
    "reflow-grid": "Update Grid (G)",
    "quick-select": "Quick Select",
    pan: "Pan",
    "zoom-to-fit": "Zoom to Fit",
    "zoom-to-fit-doc": "Zoom to Fit",
    "zoom-in": "Zoom In",
    "zoom-in-doc": "Zoom In",
    "zoom-out": "Zoom Out",
    "zoom-selected": "Zoom Selected (Z)",
    "zoom-out-doc": "Zoom Out",
    "add-point": "Add Vertex",
    "vertical-gridline": "Add Vertical Gridline",
    "horizontal-gridline": "Add Horizontal Gridline",
    "merge-cells": "Merge Cells",
    "remove-point": "Remove Vertex",
    "feature-hole": "Add Hole",
    "feature-edge-fabrication": "Add Edge Fabrication",
    "feature-corner-fabrication": "Add Corner Fabrication",
    "feature-edge-aligned-fabrication": "Add Edge Aligned Fabrication",
    location: "Add Location",
    "location-item": "Add Item Marker",
    "location-type": "Add Type Marker",
    "location-collection": UX_MODE === "shpx" ? "Add Frame Marker" : "Add Opening Marker",
    "location-document_note": "Add Note",
    "ai-clip": "AI Clip",
    "doc-filter-settings": "Filter Settings",
    ruler: "Ruler",
  };

  const toolIcons = {
    select: SelectIcon,
    search: SearchIcon,
    "reflow-grid": GridIcon,
    "quick-select": QuickSelectIcon,
    pan: PanIcon,
    "zoom-to-fit": ZoomFitIcon,
    "zoom-to-fit-doc": ZoomFitIcon,
    "zoom-in": ZoomIcon,
    "zoom-in-doc": ZoomIcon,
    "zoom-selected": ZoomSelectedIcon,
    "zoom-out": ZoomOutIcon,
    "zoom-out-doc": ZoomOutIcon,
    "add-point": PenPlusIcon,
    "vertical-gridline": VerticalGridlineIcon,
    "horizontal-gridline": HorizontalGridlineIcon,
    "merge-cells": MergeIcon,
    "remove-point": PenMinusIcon,
    "feature-hole": HoleIcon,
    "feature-edge-fabrication": NotchIcon,
    "feature-edge-aligned-fabrication": EdgeFabIcon,
    "feature-corner-fabrication": CornerNotchIcon,
    location: LocationIcon,
    "location-item": LocationItemIcon,
    "location-type": LocationTypeIcon,
    "location-collection": LocationOpeningIcon,
    "location-document_note": NotesIcon,
    "ai-clip": AiIcon,
    "doc-filter-settings": FilterIcon,
    ruler: RulerIcon,
  };

  const toolIsStateful = {
    select: true,
    pan: true,
    location: true,
    "location-item": true,
    "location-type": true,
    "location-collection": true,
    "ai-clip": true,
    "location-document_note": true,
    "add-point": true,
    "vertical-gridline": true,
    "horizontal-gridline": true,
    "merge-cells": true,
    "remove-point": true,
    "feature-hole": true,
    "feature-edge-fabrication": true,
    "feature-corner-fabrication": true,
    "feature-edge-aligned-fabrication": true,
    "dimension-linear": true,
    "dimension-rectangular": true,
  };

  const toolEditsShape = {
    "add-point": true,
    "remove-point": true,
    "feature-hole": true,
    "vertical-gridline": true,
    "horizontal-gridline": true,
    "merge-cells": true,
    "feature-edge-fabrication": true,
    "feature-corner-fabrication": true,
    "feature-edge-aligned-fabrication": true,
  };

  const tools = {
    itemsList: [["select", "zoom-in", "zoom-out"]],
    itemsEditor: [
      ["select", "zoom-to-fit", "zoom-in", "zoom-out", "pan"],
      [
        "add-point",
        "remove-point",
        "feature-hole",
        "feature-edge-fabrication",
        "feature-corner-fabrication",
        "feature-edge-aligned-fabrication",
      ],
    ],
    openingsList: [["select", "zoom-in", "zoom-out"]],
    typesList: [["select"]],
    typesEditor: [["select"]],
    fabricationsList: [["select"]],
    fabricationsEditor: [["select", "zoom-to-fit", "zoom-in", "zoom-out", "pan"]],
    openingsEditor: [
      ["select", "zoom-to-fit", "zoom-in", "zoom-out", "pan"],
      ["add-point", "remove-point", "vertical-gridline", "horizontal-gridline", "merge-cells"],
    ],
    documentsList: [["select"]],
    documentsEditor: [
      ["select", "zoom-to-fit-doc", "reflow-grid", "zoom-in-doc", "zoom-out-doc", "zoom-selected", "pan"],
      UX_MODE === "shpx"
        ? ["location-collection"]
        : ["location-item", "location-collection", "location-type", "location-document_note"],
      UX_MODE === "shpx" ? [] : ["ai-clip"],
    ],
  };

  const nonToolbarTools = {
    documentsEditor: ["dimension-linear", "dimension-rectangular"],
  };

  $: undoable = $group && group.undoable();
  $: redoable = $group && group.redoable();
  $: pathSegments = (path && path.split("/")) || [];
  $: page = `${tab}${pathSegments[1] ? "Editor" : "List"}`;
  $: canSearch = page === "documentsEditor";
  $: toolset = filterTools(page, disabled, $group?.project_type, $itemOverlayMode);
  $: statefulTools = toolset.flat().filter((tool) => toolIsStateful[tool]);
  $: statelessTools = toolset.flat().filter((tool) => !toolIsStateful[tool]);
  $: itemEditor = ["items", "openings"].includes(tab) && pathSegments[1];
  $: currentItem =
    tab === "documents" && $itemOverlay ? $group?.items[$itemOverlay] : $group?.items[itemEditor];
  $: itemUndefined = !(currentItem?.width && currentItem?.height);
  $: updateCurrentTool(toolset, page);

  function getDisabled(currentItem, toolid) {
    if (itemUndefined && toolEditsShape[toolid]) return true;

    if (toolid === "vertical-gridline" || toolid === "horizontal-gridline" || toolid === "merge-cells") {
      if (currentItem?.data.reference_planes?.type !== "grid") return true;
    }

    return false;
  }

  function filterTools(page, disabled, pt, iom) {
    if (!page) return [];
    if (!disabled && pt !== "product") {
      if (page === "documentsEditor" && iom) {
        if (iom === "item") return tools.itemsEditor;
        if (iom === "collection") return tools.openingsEditor;
      }
      return tools[page];
    }

    return tools[page].map((t) => t.filter((tool) => !toolEditsShape[tool])).filter((t) => t.length > 0);
  }

  function updateCurrentTool(toolset, page) {
    const tools = toolset.flat();
    const ntbtools = nonToolbarTools[page] || [];
    if (!tools.includes($currentTool) && !ntbtools.includes($currentTool)) {
      $currentTool = "select";
    }
  }

  function clickStatefulTool(tool) {
    if (tool === "feature-edge-fabrication") {
      fabPosition = "edge";
      const fabrications = orderedList($group.data.fabrications).filter(isEdgeFabrication);
      if (!fabrications.length) {
        fabwarning.open();
        return;
      }
    } else if (tool === "feature-corner-fabrication") {
      fabPosition = "corner";
      const fabrications = orderedList($group.data.fabrications).filter(isCornerFabrication);
      if (!fabrications.length) {
        fabwarning.open();
        return;
      }
    } else if (tool === "feature-edge-aligned-fabrication") {
      fabPosition = "edge-aligned";
      const fabrications = orderedList($group.data.fabrications).filter(isEdgeAlignedFabrication);
      if (!fabrications.length) {
        fabwarning.open();
        return;
      }
    }

    $currentTool = tool;
    eb.dispatch(tool);
  }

  onMount(async () => {
    if ($profile.organization?.org_type === "supplier") {
      quote = $group.job.quotes.find((q) => q.organization_id === $profile.organization_id);
      job = $group.job.quotes.length === 0;
    } else {
      quote = $group.job.quote_requests.find((qr) => qr.group_id === null || qr.group_id === $group.id);
    }

    eb.on("click-fab-tool", clickStatefulTool);
  });

  onDestroy(() => {
    eb.unsubscribe("click-fab-tool", clickStatefulTool);
  });
</script>

{#key $itemOverlay}
  <div class="w-full h-10 border-t border-gray-700 flex-none bg-black text-gray-200 z-10">
    <div class="flex items-center justify-between h-10 px-2">
      <div class="flex items-center flex-none gap-3">
        <div class="h-8 hidden sm:flex items-center gap-1">
          {#if tab === "documents"}
            <Tool
              toolid="panel-left"
              type="toggle"
              tooltip="Show Page List"
              state={$showLeftPanel}
              on:click={() => ($showLeftPanel = !$showLeftPanel)}>
              <PanelLeftIcon />
            </Tool>
            {#if UX_MODE === "shpx"}
              <AppsMenu />
            {/if}
          {:else}
            <Tool
              toolid="panel-left"
              type="toggle"
              tooltip="Show Table"
              state={$showTable}
              on:click={() => ($showTable = !$showTable)}>
              <TableIcon />
            </Tool>
          {/if}
        </div>

        <div class="h-8 flex items-center gap-1">
          <Tool toolid="undo" type="button" tooltip="Undo" disabled={!undoable} on:click={group.undo()}>
            <UndoIcon />
          </Tool>
          <Tool toolid="redo" type="button" tooltip="Redo" disabled={!redoable} on:click={group.redo()}>
            <RedoIcon />
          </Tool>
        </div>

        <!-- Narrow screens: collapse stateful tools into a dropdown -->
        <div class="flex sm:hidden" bind:this={toolDropdown}>
          <div class="relative mr-2">
            {#if statefulTools.length > 1}
              <div class="h-8 flex items-center">
                <Tool toolid={$currentTool} bind:state={$currentTool} tooltip={toolNames[$currentTool]}>
                  <svelte:component this={toolIcons[$currentTool]} />
                </Tool>
                <button class="text-gray-500" on:click={() => (showToolPanel = !showToolPanel)}>
                  <CaretDownIcon />
                </button>
              </div>

              {#if showToolPanel}
                <Popover container={toolDropdown}>
                  <div
                    class="tool-dropdown-container"
                    use:clickOutside={[toolDropdown]}
                    on:outclick={() => (showToolPanel = false)}>
                    {#each statefulTools as toolid}
                      {@const isDisabled = getDisabled(currentItem, toolid)}
                      <Tool
                        {toolid}
                        state={$currentTool}
                        tooltip={toolNames[toolid]}
                        on:click={() => clickStatefulTool(toolid)}
                        disabled={isDisabled}>
                        <svelte:component this={toolIcons[toolid]} />
                      </Tool>
                    {/each}
                  </div>
                </Popover>
              {/if}
            {:else}
              <div class="h-8 flex items-center">
                <Tool toolid={$currentTool} bind:state={$currentTool} tooltip={toolNames[$currentTool]}>
                  <svelte:component this={toolIcons[statefulTools[0]]} />
                </Tool>
              </div>
            {/if}
          </div>

          {#if statelessTools.length > 0}
            <div class="h-8 flex gap-1 items-center">
              {#each statelessTools as toolid}
                {@const isDisabled = getDisabled(currentItem, toolid)}
                <Tool
                  {toolid}
                  type="button"
                  tooltip={toolNames[toolid]}
                  on:click={() => eb.dispatch(toolid)}
                  disabled={isDisabled}>
                  <svelte:component this={toolIcons[toolid]} />
                </Tool>
              {/each}
            </div>
          {/if}
        </div>

        <!-- Wider screens: display tools in order given -->
        <div class="hidden sm:flex gap-3">
          {#each toolset as tools}
            <div class="h-8 flex items-center gap-1">
              {#each tools as toolid}
                {@const isStateful = toolIsStateful[toolid]}
                {@const isDisabled = getDisabled(currentItem, toolid)}
                <Tool
                  {toolid}
                  type={isStateful ? "enum" : "button"}
                  tooltip={toolNames[toolid]}
                  state={$currentTool}
                  on:click={() => (isStateful ? clickStatefulTool(toolid) : eb.dispatch(toolid))}
                  disabled={isDisabled}>
                  <svelte:component this={toolIcons[toolid]} />
                </Tool>
              {/each}
            </div>
          {/each}
        </div>
      </div>

      <div class="flex items-center flex-none gap-3">
        {#if canSearch}
          <div class="flex items-center gap-1">
            <Tool
              toolid="search"
              type="button"
              tooltip={toolNames.search}
              disabled={!!$itemOverlay}
              on:click={() => eb.dispatch("open-search")}>
              <svelte:component this={toolIcons.search} />
            </Tool>
            <Tool
              toolid="quick-select"
              type="button"
              tooltip={toolNames["quick-select"]}
              disabled={!!$itemOverlay}
              on:click={() => eb.dispatch("open-quick-select")}>
              <svelte:component this={toolIcons["quick-select"]} />
            </Tool>
            <Tool
              toolid="doc-filter-settings"
              type="button"
              tooltip={toolNames["doc-filter-settings"]}
              disabled={!!$itemOverlay}
              on:click={() => eb.dispatch("open-doc-filter-settings")}>
              <svelte:component this={toolIcons["doc-filter-settings"]} />
            </Tool>
            <Tool
              toolid="ruler"
              type="button"
              tooltip={toolNames["ruler"]}
              disabled={!!$itemOverlay}
              on:click={() => eb.dispatch("open-ruler")}>
              <svelte:component this={toolIcons["ruler"]} />
            </Tool>
            <!-- Hidden while FrameX is open: its Export Frames step is the way out,
                 and this button bypasses the stepper into the legacy download modal
                 with no scope, no format memory and no built-frame count. Gated on
                 both switches, the same pair that gates FrameXPanel itself, so the
                 legacy product and the shpx build with FrameX closed are untouched. -->
            {#if !(UX_MODE === "shpx" && $leftPanelMode === "framex")}
              <button
                class="action-button flex gap-1"
                class:disabled={!!$itemOverlay}
                on:click={() => eb.dispatch(`open-list-download`)}
                disabled={!!$itemOverlay}
                use:tooltip={{ text: "Download" }}>
                <DownloadIcon /> Download
              </button>
            {/if}
          </div>
        {/if}
        <div class="flex items-center text-xs gap-2">
          {#if !disabled && ["items", "openings"].includes(tab) && !pathSegments[1]}
            <button
              class="action-button"
              on:click={() => eb.dispatch(`open-list-download`)}
              use:tooltip={{ text: "Download" }}>
              <DownloadIcon />
            </button>
            <button
              class="action-button"
              on:click={() => eb.dispatch("import-csv")}
              use:tooltip={{ text: "Bulk Import" }}>
              <UploadIcon />
            </button>
          {/if}
          {#if quote}
            <button class="action-button flex gap-1" on:click={() => navigate(`/quotes/${quote.id}`)}>
              <ShareIcon />
              <div class="hidden md:block">Go to Overview</div>
            </button>
          {:else if job}
            <button class="action-button flex gap-1" on:click={() => navigate(`/jobs/${$group.job_id}`)}>
              <ShareIcon />
              <div class="hidden md:block">Go to Overview</div>
            </button>
          {/if}
        </div>

        <div class="flex items-center gap-1">
          {#if page === "documentsEditor"}
            <Tool
              toolid="help"
              type="button"
              tooltip="Help"
              on:click={() => ($showingTutorial = !$showingTutorial)}>
              <HelpIcon />
            </Tool>
          {/if}
          {#if $profile?.profile_capabilities?.capabilities?.edit_tally_jobs}
            <Tool
              toolid="utilities"
              type="button"
              tooltip="Utilities"
              on:click={() => eb.dispatch("open-utilities")}>
              <WrenchIcon />
            </Tool>
          {/if}
          <Tool
            toolid="settings"
            type="button"
            tooltip="Settings"
            on:click={() => eb.dispatch("open-settings")}>
            <GearIcon />
          </Tool>
          {#if $profile?.user_role === "developer"}
            <Tool
              toolid="ai-settings"
              type="button"
              tooltip="AI Settings"
              on:click={() => eb.dispatch("open-ai-settings")}>
              <AiSettingsIcon />
            </Tool>
          {/if}
        </div>

        <div class="h-8 hidden sm:flex items-center">
          <Tool
            toolid="panel-right"
            type="toggle"
            tooltip="Show Properties Panel"
            state={$showRightPanel}
            on:click={() => ($showRightPanel = !$showRightPanel)}>
            <PanelRightIcon />
          </Tool>
        </div>
      </div>
    </div>
  </div>
{/key}

<Modal bind:this={fabwarning} closeOnOutclick closeable>
  <div slot="title">Add Fabrications</div>
  <div slot="content" class="space-y-2">
    <div>
      This job does not have any valid {fabPosition} fabrications. Add a fabrication to the job before placing
      it on an item.
    </div>
  </div>
</Modal>

<style lang="scss">
  button {
    @apply opacity-50 px-2 text-white font-semibold;

    &:hover {
      @apply opacity-100;
    }
  }

  button.action-button {
    @apply opacity-100 rounded-md py-1 bg-blue-600 border border-blue-700 text-white text-xs;

    &:hover {
      @apply bg-blue-500;
    }
  }

  .tool-dropdown-container {
    @apply p-1 rounded drop-shadow-lg border border-gray-700;
    @apply bg-black text-xs text-gray-200 flex gap-1 z-50;
  }
</style>
